/* The v3 console shell: sidebar, top bar, the docked Zana panel, search and
   jump (Ctrl K), toasts and the theme. Ported from the design's shared shell;
   pages render into <main className="body">. */
import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import type { FleetModel } from './data/model';
import { useFleet } from './data/FleetData';
import { AddBusModal } from './BusModals';
import { askZana, ZanaAnswer, ZanaScreen } from './data/zana';
import { openIncidents } from './data/analytics';
import { TakeoverBody, TakeoverLayer, TakeoverProvider, takeoverHead, useTakeover } from './zana/Takeover';
import { availablePlans, planForText } from './zana/plans';
import { ExplainBody, ExplainLayer, ExplainPoint, ExplainState, PointRing } from './zana/Explain';
import { describePick, lookAtMain, pointOnScreen } from './zana/screen';
import { MAX_CHATS, ZChat, loadChats, newChat, titleOf, writeChats } from './zana/chats';
import { recallAsk, rememberAsk } from './zana/done';
import { reportZanaFailure } from './zana/failureLog';
import { pageAsked, screenNamed, ScreenGo, walkThrough } from './zana/nav';
import { recallThread, turnForModel } from './zana/thread';
import {
  IcSearch, IcX, IcBus, IcUser, IcPage, OfflineBanner, PAGES, PageKey, ToastProvider, ToastRegion,
  ZanaMark, hhmm, initials, plateBeside, plural, useGo, useOnline, useToastState, Svg,
} from './kit';

/* ── theme: follows the computer by default; the account menu offers
      System, Light and Dark. Colours cross-fade 300 ms, nothing moves. */
export type ThemePref = 'system' | 'light' | 'dark';
const THEME_KEY = 'trasse.console.theme';
function readTheme(): ThemePref {
  try { const v = localStorage.getItem(THEME_KEY); if (v === 'light' || v === 'dark' || v === 'system') return v; } catch { /* storage blocked */ }
  return 'system';
}
function useTheme() {
  const [pref, setPref] = useState<ThemePref>(readTheme);
  const [sysDark, setSysDark] = useState(() => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false);
  useEffect(() => {
    const m = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!m) return;
    const f = (e: MediaQueryListEvent) => setSysDark(e.matches);
    m.addEventListener?.('change', f);
    return () => m.removeEventListener?.('change', f);
  }, []);
  const set = useCallback((p: ThemePref) => { setPref(p); try { localStorage.setItem(THEME_KEY, p); } catch { /* ignore */ } }, []);
  return { pref, set, dark: pref === 'dark' || (pref === 'system' && sysDark) };
}

/* ── what the current page tells the shell ── */
export interface PageMeta {
  key: PageKey | 'other';
  title: string;            // crumb
  parent?: { page: PageKey; title: string }; // a page opened from another: Fuel › Fuel prices
  zanaContext: string;      // "Overview · today"
  zanaIntro: string;
  prompts: string[];
  rail?: boolean;           // live map: sidebar collapses to a rail
  zana?: ZanaScreen;        // what is on this page: selected bus, period, figures
  /** What Zana can explain on this page (elements carry data-zx). */
  points?: ExplainPoint[];
}
interface ShellApi {
  setMeta: (m: PageMeta) => void;
  /** `about`: the stat or element the question is about, shown as a chip. */
  ask: (q: string, about?: string) => void;
  /** Zana explains this page, or the owner points at something on it. */
  explain: (mode?: 'explain' | 'point') => void;
  openZana: () => void;
  openPalette: () => void;
  /** The Add a bus dialog — every "Add a bus" button in the console. */
  openAddBus: () => void;
  dark: boolean;
}
const ShellCtx = createContext<ShellApi>({ setMeta: () => undefined, ask: () => undefined, explain: () => undefined, openZana: () => undefined, openPalette: () => undefined, openAddBus: () => undefined, dark: false });
export const useShell = () => useContext(ShellCtx);
/** Pages call this once with their crumb, Zana context and prompts. */
export function usePage(meta: PageMeta) {
  const { setMeta } = useShell();
  const key = JSON.stringify(meta);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setMeta(meta); }, [key, setMeta]);
}

/* ── sidebar ── */
const NAV_ICON: Record<string, React.ReactNode> = {
  ConsoleOverview: <><path d="M3 11 12 4l9 7" /><path d="M5 10v10h14V10" /><path d="M10 20v-6h4v6" /></>,
  ConsoleLiveMap: <><path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2z" /><path d="M9 4v14M15 6v14" /></>,
  ConsoleAlerts: <><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></>,
  ConsoleBuses: <><rect x="4" y="3" width="16" height="15" rx="3" /><path d="M4 11h16M8 21v-3M16 21v-3" /><circle cx="8" cy="14.5" r=".6" /><circle cx="16" cy="14.5" r=".6" /></>,
  ConsoleFuel: <><path d="M3 22V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v17" /><path d="M3 22h12" /><path d="M15 10h2a2 2 0 0 1 2 2v5a2 2 0 0 0 4 0V9l-3-3" /><path d="M7 7h4v4H7z" /></>,
  ConsoleMaintenance: <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.4-.6-.6-2.4z" />,
  ConsoleTrips: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18" /></>,
  ConsolePeople: <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><path d="M16 4.5a3.5 3.5 0 0 1 0 7M18 14a6.5 6.5 0 0 1 3.5 6" /></>,
  ConsoleReport: <><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6M8 13h8M8 17h5" /></>,
  ConsoleAnalytics: <><path d="M3 3v18h18" /><path d="m7 15 4-5 3 3 5-7" /></>,
  ConsoleFuelPrices: <><path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z" /><circle cx="7.5" cy="7.5" r="1.5" /></>,
  ConsoleSettings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></>,
  ConsoleBilling: <><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20M6 15h4" /></>,
};

interface Counts { onRoute: number; alerts: number; buses: number; maint: number; incidents: number }
function useCounts(f: FleetModel): Counts {
  return useMemo(() => {
    const soon = Date.now() + 14 * 86400000;
    let maint = 0;
    f.buses.forEach((b) => b.papers.forEach((p) => {
      if ((p.due && p.due.getTime() < soon) || (p.dueKm != null && p.dueKm <= 0)) maint++;
    }));
    return {
      onRoute: f.buses.filter((b) => b.status === 'on_route' || b.status === 'late').length,
      alerts: f.alerts.filter((a) => !a.read && a.kind !== 'incident').length,
      buses: f.buses.length,
      maint,
      incidents: openIncidents(f).length,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.buses, f.alerts, f.incidents]);
}

const NavLink: React.FC<{ page: PageKey; label: string; on: boolean; children?: React.ReactNode }> = ({ page, label, on, children }) => {
  const go = useGo();
  return (
    <a className={'nav' + (on ? ' on' : '')} {...go(page)} aria-current={on ? 'page' : undefined} title={label}>
      <Svg size={19}>{NAV_ICON[page]}</Svg>
      <span className="lbl">{label}</span>
      {children}
    </a>
  );
};

const Sidebar: React.FC<{ active: string; rail: boolean; f: FleetModel; theme: ReturnType<typeof useTheme>; onSignOut: () => void }> = ({ active, rail, f, theme, onSignOut }) => {
  const c = useCounts(f);
  const [menu, setMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  /* The menu floats (position: fixed) from the avatar button. Inside the
     sidebar it was clipped: the sidebar scrolls, so nothing wider than it
     shows, and on the Live map the sidebar is the 68 px rail — the menu
     looked as if it sat under the map (Gabriel, 2026-10-28). */
  const btnRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState<{ left: number; bottom: number } | null>(null);
  const toggleMenu = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!menu && r) setMenuPos({ left: r.left, bottom: window.innerHeight - r.top + 6 });
    setMenu(!menu);
  };
  useEffect(() => {
    if (!menu) return;
    const close = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) setMenu(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(false); };
    const off = () => setMenu(false);
    document.addEventListener('mousedown', close); document.addEventListener('keydown', esc); window.addEventListener('resize', off);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); window.removeEventListener('resize', off); };
  }, [menu]);
  const go = useGo();
  return (
    <aside className={'side' + (rail ? ' rail' : '')} aria-label="Main">
      <div className="brand">
        <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
          <rect width="28" height="28" rx="8" style={{ fill: 'var(--ink)' }} />
          <path d="M10 7.5v10.2c0 2.2 1.2 3.3 3.3 3.3H17M7.5 11.5H17" style={{ stroke: 'var(--bg)' }} strokeWidth="2.6" fill="none" strokeLinecap="round" />
          <circle cx="20.5" cy="21" r="2.4" fill="#0FB8CE" />
        </svg>
        <span className="lbl" style={{ fontFamily: "'Barlow Semi Condensed', Arial", fontWeight: 700, fontSize: 20, letterSpacing: '-.01em' }}>Trasse</span>
      </div>
      <a className="school" {...go('ConsoleSettings')} title="School settings">
        <span className="av" style={{ width: 26, height: 26, borderRadius: 7, background: 'var(--teal-tint)', color: 'var(--teal-ink)', fontSize: 12 }}>{(f.org.name || '·').slice(0, 1).toUpperCase()}</span>
        <span style={{ display: 'flex', flexDirection: 'column', lineHeight: '16px', minWidth: 0 }}>
          <b style={{ fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.org.name || 'Your school'}</b>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>{f.org.termLabel || 'Set your term dates'}</span>
        </span>
        <span style={{ marginLeft: 'auto', color: 'var(--faint)' }}><Svg size={16}><path d="m6 9 6 6 6-6" /></Svg></span>
      </a>
      <div className="grp">Today</div>
      <NavLink page="ConsoleOverview" label="Overview" on={active === 'ConsoleOverview'} />
      <NavLink page="ConsoleLiveMap" label="Live map" on={active === 'ConsoleLiveMap'}>
        {c.onRoute > 0 && <span className="cnt" style={{ background: 'transparent', gap: 6 }}><span className="livedot" /><span className="lbl" style={{ fontWeight: 600 }}>{c.onRoute}</span></span>}
      </NavLink>
      <NavLink page="ConsoleAlerts" label="Alerts" on={active === 'ConsoleAlerts'}>
        {c.alerts > 0 && <span className="cnt bad">{c.alerts}</span>}
      </NavLink>
      <div className="grp">Fleet</div>
      <NavLink page="ConsoleBuses" label="Buses" on={active === 'ConsoleBuses'}>
        {c.buses > 0 && <span className="cnt">{c.buses}</span>}
      </NavLink>
      {/* Fuel prices opens from a button on Fuel (Gabriel, 2026-10-28), so Fuel stays lit there. */}
      <NavLink page="ConsoleFuel" label="Fuel" on={active === 'ConsoleFuel' || active === 'ConsoleFuelPrices'} />
      <NavLink page="ConsoleMaintenance" label="Maintenance" on={active === 'ConsoleMaintenance'}>
        {c.maint > 0 && <span className="cnt">{c.maint}</span>}
      </NavLink>
      <NavLink page="ConsoleTrips" label="Trips" on={active === 'ConsoleTrips'} />
      <div className="grp">People</div>
      <NavLink page="ConsolePeople" label="Drivers and parents" on={active === 'ConsolePeople'} />
      <div className="grp">Reports</div>
      <NavLink page="ConsoleAnalytics" label="Analytics" on={active === 'ConsoleAnalytics'}>
        {c.incidents > 0 && <span className="cnt bad" title={plural(c.incidents, 'open incident')}>{c.incidents}</span>}
      </NavLink>
      <NavLink page="ConsoleReport" label="Monthly report" on={active === 'ConsoleReport'} />
      <div style={{ flex: 1, minHeight: 24 }} />
      <div className="sfoot" style={{ display: 'flex', flexDirection: 'column', gap: 2, borderTop: '1px solid var(--line)', paddingTop: 10, position: 'relative' }} ref={menuRef}>
        <NavLink page="ConsoleSettings" label="Settings" on={active === 'ConsoleSettings'} />
        <NavLink page="ConsoleBilling" label="Plan and billing" on={active === 'ConsoleBilling'} />
        <button type="button" className="row" ref={btnRef} onClick={toggleMenu} aria-haspopup="menu" aria-expanded={menu}
          style={{ padding: '10px 6px 2px', border: 0, background: 'transparent', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
          <span className="av" style={{ background: '#DCE7EA', color: '#2B323B' }}>{initials(f.me.name)}</span>
          <span className="lbl" style={{ display: 'flex', flexDirection: 'column', lineHeight: '17px', minWidth: 0 }}>
            <b style={{ fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.me.name}</b>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>{f.me.roleLabel}</span>
          </span>
        </button>
        {menu && (
          <div role="menu" className="panel up" style={{ position: 'fixed', left: menuPos?.left ?? 12, bottom: menuPos?.bottom ?? 72, width: 220, padding: 6, boxShadow: 'var(--float)', zIndex: 35 }}>
            <p className="mono" style={{ margin: '6px 10px 4px', textTransform: 'uppercase', letterSpacing: '.08em', fontSize: 11 }}>Theme</p>
            {(['system', 'light', 'dark'] as ThemePref[]).map((p) => (
              <button key={p} type="button" role="menuitemradio" aria-checked={theme.pref === p} className="pal-i" style={{ height: 38 }} onClick={() => theme.set(p)}>
                <span style={{ width: 16, color: 'var(--teal-ink)' }}>{theme.pref === p ? <Svg size={16}><path d="m5 12 5 5 9-10" /></Svg> : null}</span>
                <span>{p === 'system' ? 'System' : p === 'light' ? 'Light' : 'Dark'}</span>
              </button>
            ))}
            <div style={{ borderTop: '1px solid var(--line)', margin: '6px 0' }} />
            {String(f.org.raw.subscriptionPlan || f.org.raw.plan || '').toLowerCase() === 'enterprise' && (
              <a role="menuitem" className="pal-i" style={{ height: 38 }} {...go('ConsoleBilling', '#branches')} onClickCapture={() => setMenu(false)}>Switch branch</a>
            )}
            <button type="button" role="menuitem" className="pal-i" style={{ height: 38 }} onClick={onSignOut}>Sign out</button>
          </div>
        )}
      </div>
    </aside>
  );
};

/* ── search and jump (Ctrl K). Results update instantly, with no animation. */
interface PalItem { group: 'Buses' | 'People' | 'Ask Zana' | 'Go to'; icon: React.ReactNode; title: React.ReactNode; small?: string; run: () => void }
const GO_TO: { page: PageKey; label: string; keys?: string }[] = [
  { page: 'ConsoleOverview', label: 'Overview', keys: 'G then O' },
  { page: 'ConsoleLiveMap', label: 'Live map', keys: 'G then M' },
  { page: 'ConsoleAlerts', label: 'Alerts', keys: 'G then A' },
  { page: 'ConsoleBuses', label: 'Buses', keys: 'G then B' },
  { page: 'ConsoleFuel', label: 'Fuel', keys: 'G then F' },
  { page: 'ConsoleMaintenance', label: 'Maintenance' },
  { page: 'ConsoleTrips', label: 'School trips', keys: 'G then T' },
  { page: 'ConsolePeople', label: 'Drivers and parents', keys: 'G then P' },
  { page: 'ConsoleAnalytics', label: 'Analytics', keys: 'G then N' },
  { page: 'ConsoleFuelPrices', label: 'Fuel prices' },
  { page: 'ConsoleReport', label: 'Monthly report', keys: 'G then R' },
  { page: 'ConsoleSettings', label: 'Generate a sign-up code', keys: 'Settings' },
  { page: 'ConsoleBilling', label: 'Plan and billing' },
  { page: 'ConsoleZanaChat', label: 'Zana · full page', keys: 'G then Z' },
];
const G_KEYS: Record<string, PageKey> = { o: 'ConsoleOverview', m: 'ConsoleLiveMap', a: 'ConsoleAlerts', b: 'ConsoleBuses', f: 'ConsoleFuel', t: 'ConsoleTrips', p: 'ConsolePeople', r: 'ConsoleReport', n: 'ConsoleAnalytics', s: 'ConsoleSettings', z: 'ConsoleZanaChat' };

const Palette: React.FC<{ f: FleetModel; onClose: () => void; onAsk: (q: string) => void }> = ({ f, onClose, onAsk }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const opener = useRef<Element | null>(document.activeElement);
  useEffect(() => { input.current?.focus(); const o = opener.current; return () => { (o as HTMLElement | null)?.focus?.(); }; }, []);
  const items = useMemo<PalItem[]>(() => {
    const t = q.trim().toLowerCase();
    const out: PalItem[] = [];
    if (t) {
      f.buses.filter((b) => [b.name, b.plate, b.driverName].some((x) => x.toLowerCase().includes(t))).slice(0, 5).forEach((b) => out.push({
        group: 'Buses', icon: <IcBus />, title: <span><b>{b.name}</b>{plateBeside(b) ? <> <span className="mono">{b.plate}</span></> : null}</span>, small: b.needsLook || b.statusLabel,
        run: () => navigate(`${PAGES.ConsoleBuses}?bus=${encodeURIComponent(b.id)}`),
      }));
      f.drivers.concat(f.parents).filter((p) => [p.name, p.phone, p.email, ...p.children.map((c) => c.name)].some((x) => x.toLowerCase().includes(t))).slice(0, 5).forEach((p) => out.push({
        group: 'People', icon: <IcUser />, title: <span><b>{p.name}</b>{p.children.length ? <span className="mono"> · {p.children.map((c) => c.name).join(', ')}</span> : null}</span>,
        small: p.role === 'driver' ? (p.busName ? `Driver · ${p.busName}` : 'Driver') : 'Parent',
        run: () => navigate(`${PAGES.ConsolePeople}?tab=${p.role === 'driver' ? 'drivers' : 'parents'}&q=${encodeURIComponent(p.name)}`),
      }));
      out.push({ group: 'Ask Zana', icon: <ZanaMark />, title: <span>{q.trim()}</span>, small: 'Enter', run: () => onAsk(q.trim()) });
    }
    GO_TO.filter((g) => !t || g.label.toLowerCase().includes(t)).slice(0, t ? 4 : 6).forEach((g) => out.push({ group: 'Go to', icon: <IcPage />, title: <span>{g.label}</span>, small: g.keys, run: () => navigate(PAGES[g.page]) }));
    return out;
  }, [q, f.buses, f.drivers, f.parents, navigate, onAsk]);
  useEffect(() => setHi(0), [q]);
  const run = (i: PalItem) => { onClose(); i.run(); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(items.length - 1, h + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
    else if (e.key === 'Enter' && items[hi]) { e.preventDefault(); run(items[hi]); }
  };
  const groups = ['Buses', 'People', 'Ask Zana', 'Go to'] as const;
  let idx = -1;
  const searched = q.trim() && !items.some((i) => i.group === 'Buses' || i.group === 'People');
  return (
    <div className="scrim" onClick={onClose}>
      <div className="pal" role="dialog" aria-modal="true" aria-label="Search and jump" onClick={(e) => e.stopPropagation()} onKeyDown={onKey}>
        <div className="pal-in">
          <span style={{ color: 'var(--muted)' }}><IcSearch size={19} /></span>
          <input ref={input} type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search buses, children, drivers…" aria-label="Search"
            style={{ flex: 1, border: 0, outline: 0, font: 'inherit', fontSize: 17, background: 'transparent', color: 'var(--ink)', padding: 0 }} />
          <span className="kbd">Esc</span>
        </div>
        {searched && (
          <p className="sm" style={{ margin: 0, padding: '12px 18px 0', color: 'var(--muted)' }}>No bus, driver or child matches “{q.trim()}”. Check the plate, or search by driver or route name.</p>
        )}
        {groups.map((g) => {
          const list = items.filter((i) => i.group === g);
          if (!list.length) return null;
          return (
            <div className="pal-g" key={g} style={g !== 'Buses' ? { borderTop: '1px solid var(--line)' } : undefined}>
              <p>{g}</p>
              {list.map((i) => { idx++; const me = idx; return (
                <button key={me} type="button" className={'pal-i' + (me === hi ? ' hi' : '')} onMouseEnter={() => setHi(me)} onClick={() => run(i)}>
                  <span style={{ color: 'var(--muted)', display: 'inline-flex' }}>{i.icon}</span>{i.title}{i.small && <small>{i.small}</small>}
                </button>
              ); })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ── Zana panel: answers from your records, named steps while she works. */
type ZState = { s: 'idle' } | { s: 'thinking'; q: string; step: number; about?: string } | { s: 'answer'; q: string; a: ZanaAnswer; about?: string } | { s: 'error'; q: string; about?: string };
const STEPS = (ctx: string) => ['Reading your records…', `Looking at ${ctx.split(' · ')[0].toLowerCase()}…`, 'Checking the latest trips…', 'Writing the answer…'];

/** The same thin marks as the end of a Cursor message: useful, not useful, copy. */
const RateIcon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const ZanaPanel: React.FC<{
  open: boolean; meta: PageMeta; z: ZState; onClose: () => void; onAdd: () => void; onAsk: (q: string, about?: string) => void;
  ex: ExplainState | null; setEx: (e: ExplainState | null) => void;
  chats: ZChat[]; activeId: string | null; onPick: (id: string) => void; onCloseChat: (id: string) => void;
  chatLimit: boolean; onDraft: (text: string) => void;
  wide: boolean; onWide: () => void;
  onRate: (at: number, rating: 'good' | 'bad') => void;
}> = ({ open, meta, z, onClose, onAdd, onAsk, ex, setEx, chats, activeId, onPick, onCloseChat, chatLimit, onDraft, wide, onWide, onRate }) => {
  const active = chats.find((c) => c.id === activeId) || null;
  const draft = active?.draft || '';
  const go = useGo();
  const take = useTakeover();
  const tk = take?.tk || null;
  // What Zana can take over right now, from the live records.
  const plans = useMemo(() => (take && open && !tk ? availablePlans(take.ctx) : []), [take, open, tk]);
  const points = meta.points || [];
  const busy = !!tk && tk.phase !== 'plan' && tk.phase !== 'done' && tk.phase !== 'stopped' && tk.phase !== 'offer';
  const askRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = askRef.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = Math.min(el.scrollHeight, 132) + 'px';
  }, [draft]);
  const send = () => {
    const t = draft.trim(); if (!t || busy) return;
    onDraft('');
    if (tk && !tk.approved) take?.cancel();
    onAsk(t);
  };
  const steps = STEPS(meta.zanaContext || meta.title);
  const messages = active?.messages || [];
  const [copiedAt, setCopiedAt] = useState<number | null>(null);
  const zanaAt = new Map<number, number>();
  messages.forEach((m, i) => { if (m.from === 'zana') zanaAt.set(i, zanaAt.size + 1); });
  return (
    <aside className={'zp' + (wide ? ' full' : '')} style={{ width: open ? (wide ? undefined : 380) : 0, borderLeftWidth: open ? undefined : 0 }} aria-label="Zana" aria-hidden={!open}>
      {open && (
        <div className="zin" style={wide ? { width: '100%' } : undefined}>
          <div className="zhead">
            <ZanaMark size={26} />
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '17px' }}>
              <b style={{ fontSize: 15 }}>Zana</b>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>{tk ? takeoverHead(tk) : ex ? (ex.mode === 'point' ? 'Pointing mode' : ex.step ? `Explaining · step ${ex.step} of ${points.length}` : 'Done explaining') : 'Your fleet assistant'}</span>
            </div>
            <div className="zmini">
              <button type="button" className="ibtn" aria-label={wide ? 'Shrink Zana' : 'Expand Zana'} title={wide ? 'Shrink' : 'Expand'} aria-pressed={wide} onClick={onWide}><Svg size={18}><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></Svg></button>
              <button type="button" className="ibtn" aria-label="New conversation" title={chats.length >= MAX_CHATS ? 'Close a chat first. Five is the limit.' : 'New conversation'} onClick={onAdd}><Svg size={18}><path d="M12 5v14M5 12h14" /></Svg></button>
              <button type="button" className="ibtn" aria-label="Close Zana" onClick={onClose}><IcX size={18} /></button>
            </div>
          </div>
          {chats.length > 0 && (
            <div className="ztabs" role="tablist" aria-label="Zana chats">
              {chats.map((c) => (
                <span key={c.id} className={'ztab' + (c.id === activeId ? ' on' : '')}>
                  <button type="button" className="ztab-l" role="tab" aria-selected={c.id === activeId} onClick={() => onPick(c.id)}>{c.title}</button>
                  <button type="button" className="ztab-x" aria-label={`Close ${c.title}`} onClick={() => onCloseChat(c.id)}><IcX size={12} /></button>
                </span>
              ))}
            </div>
          )}
          <div className="zctx">
            <span className="chip c-z" style={{ height: 24 }}>Looking at</span>
            <span style={{ fontWeight: 600, color: 'var(--ink2)' }}>{meta.zanaContext || meta.title}</span>
          </div>
          <div className="zbody" aria-live="polite">
            {chatLimit && <p className="sm" style={{ margin: 0, color: 'var(--warn)' }}>Five chats are open. Close one to start another.</p>}
            {messages.map((m, i) => (m.from === 'you' ? (
              <div key={`${m.at}-${i}`} className="zq">{m.text}</div>
            ) : (
              <div key={`${m.at}-${i}`} className="zcard">
                <p style={{ margin: 0, fontSize: 16, lineHeight: '23px', fontWeight: 600, whiteSpace: 'pre-line' }}>{m.headline || m.text}</p>
                {!!m.facts?.length && <ul className="zfacts">{m.facts.map((x, j) => <li key={j}>{x}</li>)}</ul>}
                {m.note && <p className="sm" style={{ margin: 0, color: 'var(--muted)' }}>{m.note}</p>}
                {!!m.sources?.length && (
                  <div className="zsrc">{m.sources.map((s, j) => <a key={j} {...go(s.page as PageKey, s.search)}><IcPage size={14} />{s.label}</a>)}</div>
                )}
                <div className="zrate">
                  <button type="button" className={m.rating === 'good' ? 'on' : ''} aria-label="Useful" aria-pressed={m.rating === 'good'} onClick={() => onRate(m.at, 'good')}><RateIcon><path d="M7 10v12" /><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" /></RateIcon></button>
                  <button type="button" className={m.rating === 'bad' ? 'on' : ''} aria-label="Not useful" aria-pressed={m.rating === 'bad'} onClick={() => onRate(m.at, 'bad')}><RateIcon><path d="M17 14V2" /><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" /></RateIcon></button>
                  <button type="button" aria-label={copiedAt === m.at ? 'Copied' : 'Copy answer'} onClick={() => {
                    const text = [m.headline || m.text, ...(m.facts || []), m.note].filter(Boolean).join('\n');
                    void navigator.clipboard?.writeText(text).then(() => { setCopiedAt(m.at); window.setTimeout(() => setCopiedAt((cur) => (cur === m.at ? null : cur)), 1200); });
                  }}>{copiedAt === m.at ? <RateIcon><path d="M20 6 9 17l-5-5" /></RateIcon> : <RateIcon><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></RateIcon>}</button>
                </div>
                {(zanaAt.get(i) || 0) % 5 === 0 && !m.rating && (
                  <p className="sm" style={{ margin: 0, color: 'var(--faint)' }}>A useful or not-useful mark tells me which threads to keep.</p>
                )}
              </div>
            )))}
            {tk && <TakeoverBody />}
            {!tk && ex && points.length > 0 && <ExplainBody points={points} ex={ex} set={setEx} onAsk={onAsk} />}
            {!tk && !(ex && points.length > 0) && <>
            {z.s === 'idle' && messages.length === 0 && (
              <div className="zin-a" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <p style={{ margin: '0 0 4px', color: 'var(--ink2)' }}>{meta.zanaIntro || 'Ask about anything on this page. I can explain a number, find a child or a bus, or draft a note to parents.'}</p>
                {meta.prompts.map((p) => (
                  <button key={p} type="button" className="zp-prompt" onClick={() => onAsk(p)}>
                    <span style={{ color: 'var(--z)' }}><Svg size={16}><path d="M5 12h14M13 6l6 6-6 6" /></Svg></span>{p}
                  </button>
                ))}
                {points.length > 0 && (
                  <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
                    <button type="button" className="btn s b-line" onClick={() => setEx({ mode: 'explain', step: 1, pick: 0 })}><ZanaMark />Explain this page</button>
                    <button type="button" className="btn s b-line" onClick={() => setEx({ mode: 'point', step: 0, pick: 0 })}>
                      <Svg size={16}><path d="M9 11V5a2 2 0 0 1 4 0v6M13 10V8a2 2 0 0 1 4 0v4M17 11a2 2 0 0 1 4 0v3a7 7 0 0 1-7 7h-1a7 7 0 0 1-6-3.5L4.5 14a2 2 0 0 1 3.4-2L9 13.5" /></Svg>I point
                    </button>
                  </div>
                )}
                {plans.length > 0 && take && <>
                  <p className="mono" style={{ margin: '10px 2px 0', textTransform: 'uppercase', letterSpacing: '.08em', fontSize: 11 }}>I can do these for you</p>
                  {plans.slice(0, 3).map((p) => (
                    <button key={p.id} type="button" className="zp-prompt" onClick={() => take.start(p)}>
                      <span className="chip c-z" style={{ height: 20, fontSize: 11, flex: 'none' }}>Takeover</span>{p.ask}
                    </button>
                  ))}
                </>}
              </div>
            )}
            {z.s === 'thinking' && (
              <div className="zcard zin-a">
                <div className="think"><ZanaMark /><span>{steps[Math.min(z.step, steps.length - 1)]}</span></div>
                <div className="shim zshim" style={{ height: 12, width: '92%' }} />
                <div className="shim zshim" style={{ height: 12, width: '70%' }} />
                <div className="shim zshim" style={{ height: 12, width: '80%' }} />
              </div>
            )}
            {z.s === 'error' && (
              <div className="zcard zin-a">
                <div className="think"><ZanaMark /><b style={{ color: 'var(--ink)' }}>I couldn’t answer that</b></div>
                <p className="sm" style={{ margin: 0, color: 'var(--muted)' }}>Your question is saved. A note of the question, this page and the time is saved for the Trasse team.</p>
                <div className="zacts"><button type="button" className="btn s b-ink" onClick={() => onAsk(z.q)}>Try again</button></div>
              </div>
            )}
            </>}
          </div>
          <div className="zfoot">
            <form className={'zinput zask' + (tk?.practice && tk.lesson === 1 ? ' spot' : '')} onSubmit={(e) => { e.preventDefault(); send(); }}>
              <textarea id="zana-ask" ref={askRef} rows={1} placeholder={busy ? 'Pause me first to ask something else…' : 'Ask, or say “do it for me”…'} aria-label="Ask Zana" value={draft} onChange={(e) => onDraft(e.target.value)} disabled={busy} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
              <button type="submit" className="btn s b-ink" aria-label="Send" style={{ width: 34, padding: 0 }} disabled={!draft.trim()}>
                <Svg size={16}><path d="M12 19V5M5 12l7-7 7 7" /></Svg>
              </button>
            </form>
            <p className="sm" style={{ margin: '8px 2px 0', color: 'var(--faint)' }}>Zana reads your fleet records. She never changes anything without asking.</p>
          </div>
        </div>
      )}
    </aside>
  );
};

/** Hands the takeover's controls to the shell, which sits above its provider. */
const TkBridge: React.FC<{ to: React.MutableRefObject<ReturnType<typeof useTakeover>> }> = ({ to }) => {
  to.current = useTakeover();
  return null;
};

/* ── the shell ── */
const DEFAULT_META: PageMeta = { key: 'other', title: '', zanaContext: '', zanaIntro: '', prompts: [] };

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const f = useFleet();
  const { logout, currentUser } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const theme = useTheme();
  const online = useOnline();
  const toasts = useToastState();
  const [meta, setMetaState] = useState<PageMeta>(DEFAULT_META);
  const [zOpen, setZOpen] = useState(false);
  const [pal, setPal] = useState(false);
  const [z, setZ] = useState<ZState>({ s: 'idle' });
  const zRun = useRef(0);
  const metaRef = useRef(meta); metaRef.current = meta;
  const openZanaCb = useCallback(() => setZOpen(true), []);
  const fRef = useRef(f); fRef.current = f;
  const uidRef = useRef<string | null>(null); uidRef.current = currentUser?.uid || null;
  const chatsRef = useRef<ZChat[]>([]);
  const activeRef = useRef<string | null>(null);
  const [chats, setChats] = useState<ZChat[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [chatLimit, setChatLimit] = useState(false);
  const [zWide, setZWide] = useState(false);
  const [pickEl, setPickEl] = useState<{ el: HTMLElement; label: string } | null>(null);
  const [pointEl, setPointEl] = useState<{ el: HTMLElement; label: string } | null>(null);
  const selectionRef = useRef<string | null>(null);
  /** Set just before an ask that came from a double-click, so that ask does not clear the pill. */
  const holdPick = useRef(false);
  const pickRef = useRef(pickEl); pickRef.current = pickEl;

  const commitChats = (next: ZChat[], id?: string | null) => {
    chatsRef.current = next;
    setChats(next);
    if (id !== undefined) { activeRef.current = id; setActiveId(id); }
    const org = fRef.current.org.code;
    const uid = uidRef.current;
    if (org && uid) writeChats(org, uid, next);
  };
  const commitRef = useRef(commitChats);
  commitRef.current = commitChats;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  /** A reply she already has: no model call. The question and the answer are both saved. */
  const noteInChat = (you: string, zana: string) => {
    let list = chatsRef.current;
    let id = activeRef.current;
    if (!id || !list.some((c) => c.id === id)) {
      if (list.length >= MAX_CHATS) { setChatLimit(true); id = list[0]?.id || null; }
      else { const c = newChat(); list = [c, ...list]; id = c.id; }
    }
    if (!id) return;
    const now = Date.now();
    const next = list.map((c) => (c.id === id ? {
      ...c, draft: '', updatedAt: now,
      title: titleOf([...c.messages, { from: 'you', text: you, at: now }]),
      messages: [...c.messages,
        { from: 'you' as const, text: you, at: now },
        { from: 'zana' as const, text: zana, at: now + 1, headline: zana },
      ],
    } : c));
    commitRef.current(next, id);
    setZ({ s: 'idle' });
  };

  useEffect(() => {
    const org = f.org.code;
    const uid = currentUser?.uid || '';
    if (!org || !uid) return;
    const list = loadChats(org, uid);
    chatsRef.current = list;
    activeRef.current = list[0]?.id || null;
    setChats(list);
    setActiveId(list[0]?.id || null);
  }, [f.org.code, currentUser?.uid]);

  const setMeta = useCallback((m: PageMeta) => setMetaState(m), []);
  const [ex, setEx] = useState<ExplainState | null>(null);
  const exRef = useRef(ex); exRef.current = ex;
  /** The takeover, reached from here through the bridge below the provider. */
  const tkRef = useRef<ReturnType<typeof useTakeover>>(null);
  const thereScreen = (about?: string): ScreenGo | null => {
    const chat = chatsRef.current.find((c) => c.id === activeRef.current);
    const last = [...(chat?.messages || [])].reverse().find((m) => m.from === 'zana');
    const pick = exRef.current;
    const point = pick ? metaRef.current.points?.[pick.pick] : undefined;
    const named = screenNamed(about || '')
      || screenNamed(selectionRef.current || '')
      || screenNamed(last?.headline || last?.text || '')
      || screenNamed(metaRef.current.title)
      || screenNamed(metaRef.current.zanaContext);
    if (named) return named;
    if (point?.open) return { page: point.open.page, href: PAGES[point.open.page] + (point.open.search || ''), name: point.open.label };
    if (metaRef.current.key !== 'other') return { page: metaRef.current.key, href: PAGES[metaRef.current.key], name: metaRef.current.title || 'This page' };
    return null;
  };
  const ask = useCallback((q: string, about?: string) => {
    let text = q.trim();
    if (!text) return;
    setZOpen(true); setPal(false); setPointEl(null);
    if (!holdPick.current) { setPickEl(null); selectionRef.current = null; }
    holdPick.current = false;
    const take = tkRef.current;
    const walk = walkThrough(text);
    if (walk) {
      if (walk.screen) {
        if (take?.tk?.phase === 'run') take.pause();
        navigateRef.current(walk.screen.href);
      }
      text = walk.question;
    }
    const go = walk ? null : (pageAsked(text) || (/\btake me there\b/i.test(text) ? thereScreen(about) : null));
    if (go) {
      if (take?.tk?.phase === 'run') take.pause();
      navigateRef.current(go.href);
      setEx(null);
      noteInChat(text, `${go.name} is open.`);
      return;
    }
    if (/\btake me there\b/i.test(text)) {
      setEx(null);
      noteInChat(text, 'Tell me which screen — Maintenance, Fuel, Buses, Alerts, or the live map.');
      return;
    }
    const plan = take && !about ? planForText(take.ctx, text) : null;
    if (take && plan) { setEx(null); take.start(plan); return; }
    if (take?.tk && !take.tk.approved) take.cancel();
    setEx(null);
    const org = fRef.current.org.code;
    const uid = uidRef.current || '';
    if (org && uid && !/\b(again|retry)\b/i.test(text)) {
      const known = recallAsk(org, uid, text);
      if (known) {
        noteInChat(text, `We already went through this. ${known.summary}`);
        const hit = pointOnScreen(known.summary, document.querySelector('.tc .main .body'), metaRef.current.points || []);
        if (hit) setPointEl(hit);
        return;
      }
    }
    const priorMsgs = chatsRef.current.find((c) => c.id === activeRef.current)?.messages || [];
    const fromThread = recallThread(text, priorMsgs);
    if (fromThread) { noteInChat(text, fromThread); return; }
    const sel = about || selectionRef.current;
    const sent = sel && !text.toLowerCase().includes(sel.toLowerCase()) ? `${text}\n\nSelected on the page: ${sel}` : text;
    let list = chatsRef.current;
    let id = activeRef.current;
    if (!id || !list.some((c) => c.id === id)) {
      if (list.length >= MAX_CHATS) { setChatLimit(true); id = list[0]?.id || null; }
      else { const c = newChat(); list = [c, ...list]; id = c.id; }
    }
    if (!id) return;
    const prior = (list.find((c) => c.id === id)?.messages || []).slice(-8).map((m) => ({ from: m.from, text: turnForModel(m) }));
    const now = Date.now();
    list = list.map((c) => (c.id === id ? {
      ...c, draft: '', updatedAt: now,
      title: titleOf([...c.messages, { from: 'you', text, at: now }]),
      messages: [...c.messages, { from: 'you' as const, text, at: now }],
    } : c));
    commitRef.current(list, id);
    const chatId = id;
    const run = ++zRun.current;
    setZ({ s: 'thinking', q: text, step: 0, about: sel || undefined });
    const tick = setInterval(() => setZ((cur) => (cur.s === 'thinking' && zRun.current === run ? { ...cur, step: cur.step + 1 } : cur)), 900);
    const look = lookAtMain(document.querySelector('.tc .main .body'), metaRef.current.points || []);
    const askedOn = window.location.pathname;
    askZana(sent, metaRef.current, fRef.current, prior, look)
      .then((a) => {
        if (zRun.current !== run) return;
        setZ({ s: 'idle' });
        if (window.location.pathname === askedOn) {
          const hit = pointOnScreen(`${a.headline}\n${(a.facts || []).join('\n')}\n${a.raw}`, document.querySelector('.tc .main .body'), metaRef.current.points || []);
          if (hit) setPointEl(hit);
        }
        const summary = ([a.headline, ...(a.facts || [])].filter(Boolean).join(' · ') || a.raw).slice(0, 500);
        rememberAsk(fRef.current.org.code, uidRef.current || '', text, summary);
        const cur = chatsRef.current.map((c) => (c.id === chatId ? {
          ...c, updatedAt: Date.now(),
          messages: [...c.messages, {
            from: 'zana' as const, text: a.raw, at: Date.now(), headline: a.headline, facts: a.facts, note: a.note,
            sources: a.sources.map((s) => ({ label: s.label, page: s.page, search: s.search })),
          }],
        } : c));
        commitRef.current(cur);
      })
      .catch(() => {
        if (zRun.current === run) setZ({ s: 'error', q: text, about: sel || undefined });
        void reportZanaFailure({
          orgCode: fRef.current.org.code,
          ask: text,
          step: 'Answer the question',
          page: metaRef.current.key,
          where: metaRef.current.zanaContext,
          phase: 'answer',
          error: 'The answer did not come back.',
          path: window.location.pathname + window.location.search,
          ui: metaRef.current.zanaContext,
        });
      })
      .finally(() => clearInterval(tick));
  }, []);
  const onDraft = useCallback((text: string) => {
    let list = chatsRef.current;
    let id = activeRef.current;
    if (!text && !id) return;
    if (!id || !list.some((c) => c.id === id)) {
      if (!text.trim()) return;
      if (list.length >= MAX_CHATS) { setChatLimit(true); return; }
      const c = { ...newChat(), draft: text, title: titleOf([], text) };
      commitRef.current([c, ...list], c.id);
      setChatLimit(false);
      return;
    }
    const next = list.map((c) => (c.id === id ? {
      ...c, draft: text, updatedAt: Date.now(),
      title: c.messages.some((m) => m.from === 'you') ? c.title : titleOf([], text),
    } : c));
    commitRef.current(next);
  }, []);
  const onRate = useCallback((at: number, rating: 'good' | 'bad') => {
    const id = activeRef.current;
    if (!id) return;
    const next = chatsRef.current.map((c) => (c.id === id ? {
      ...c,
      messages: c.messages.map((m) => (m.at === at && m.from === 'zana'
        ? { ...m, rating: m.rating === rating ? undefined : rating }
        : m)),
    } : c));
    commitRef.current(next);
  }, []);
  const onAdd = useCallback(() => {
    tkRef.current?.cancel();
    zRun.current += 1;
    setZ({ s: 'idle' });
    setEx(null);
    if (chatsRef.current.length >= MAX_CHATS) { setChatLimit(true); return; }
    const c = newChat();
    commitRef.current([c, ...chatsRef.current], c.id);
    setChatLimit(false);
    setZOpen(true);
  }, []);
  const onPick = useCallback((id: string) => { activeRef.current = id; setActiveId(id); setChatLimit(false); }, []);
  const onCloseChat = useCallback((id: string) => {
    const next = chatsRef.current.filter((c) => c.id !== id);
    const nid = activeRef.current === id ? (next[0]?.id || null) : activeRef.current;
    commitRef.current(next, nid);
    setChatLimit(false);
  }, []);
  const explain = useCallback((mode: 'explain' | 'point' = 'explain') => {
    tkRef.current?.cancel();
    setZOpen(true); setPal(false);
    setEx({ mode, step: mode === 'explain' ? 1 : 0, pick: 0 });
  }, []);
  // Keyboard: Ctrl/Cmd K opens search, "G then X" jumps, Esc closes Zana.
  useEffect(() => {
    let g = 0;
    const typing = (t: EventTarget | null) => { const el = t as HTMLElement | null; return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable); };
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPal((p) => !p); return; }
      if (e.key === 'Escape' && pickRef.current) { setPickEl(null); selectionRef.current = null; return; }
      if (e.key === 'Escape' && exRef.current) { setEx(null); return; }
      if (e.key === 'Escape' && !document.querySelector('.tc .scrim, .tc .sheet, .tc .ann')) { setZOpen(false); return; }
      if (typing(e.target) || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key.toLowerCase() === 'g') { g = Date.now(); return; }
      if (g && Date.now() - g < 1200 && G_KEYS[e.key.toLowerCase()]) { e.preventDefault(); g = 0; navigate(PAGES[G_KEYS[e.key.toLowerCase()]]); return; }
      g = 0;
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  const active = meta.key;
  // A new page has its own points: explaining stops when the page changes.
  useEffect(() => { setEx(null); setPickEl(null); setPointEl(null); selectionRef.current = null; }, [loc.pathname]);
  // Incident alerts live in Analytics → Incidents, with their own count.
  const unread = f.alerts.filter((a) => !a.read && a.kind !== 'incident').length;
  /* On Zana's full page the side panel only repeats it: close it, whichever
     way the page was opened (Gabriel, 2026-10-28). */
  useEffect(() => { if (active === 'ConsoleZanaChat') setZOpen(false); }, [active]);
  const go = useGo();
  const [addBusOpen, setAddBusOpen] = useState(false);
  const api = useMemo<ShellApi>(() => ({ setMeta, ask, explain, openZana: () => setZOpen(true), openPalette: () => setPal(true), openAddBus: () => setAddBusOpen(true), dark: theme.dark }), [setMeta, ask, explain, theme.dark]);
  // Signing out lands on the new sign-in page (Gabriel, 2026-10-28).
  const onSignOut = async () => { try { await logout(); } finally { navigate('/sign-in', { replace: true }); } };
  const bodyRef = useRef<HTMLElement>(null);
  useEffect(() => { bodyRef.current?.scrollTo?.(0, 0); }, [loc.pathname]);
  useEffect(() => {
    const onDbl = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const t = e.target as HTMLElement | null;
      if (!t || t.closest('button, a, input, textarea, select, .zp, .zx-layer, .tk-layer, .ann')) return;
      const body = bodyRef.current;
      if (!body || !body.contains(t)) return;
      const picked = describePick(t, body, metaRef.current.points || []);
      if (!picked) return;
      e.preventDefault();
      setEx(null);
      holdPick.current = true;
      setPickEl({ el: picked.el, label: picked.label });
      ask(picked.question);
    };
    document.addEventListener('dblclick', onDbl);
    return () => document.removeEventListener('dblclick', onDbl);
  }, []);

  return (
    <ShellCtx.Provider value={api}>
      <ToastProvider value={toasts.api}>
       <TakeoverProvider onOpen={openZanaCb}>
        <TkBridge to={tkRef} />
        <div className={'tc tc-app' + (theme.dark ? ' dark' : '')}>
          <div className="app">
            <Sidebar active={active} rail={!!meta.rail} f={f} theme={theme} onSignOut={onSignOut} />
            <div className={'main' + (zOpen && zWide ? ' zgone' : '')}>
              <header className="top">
                <div className="crumb">
                  <span>{f.org.name || 'Trasse'}</span>
                  <span style={{ color: 'var(--faint)' }}><Svg size={14}><path d="m9 6 6 6-6 6" /></Svg></span>
                  {meta.parent && <>
                    <a {...go(meta.parent.page)} style={{ color: 'inherit', textDecoration: 'none' }}>{meta.parent.title}</a>
                    <span style={{ color: 'var(--faint)' }}><Svg size={14}><path d="m9 6 6 6-6 6" /></Svg></span>
                  </>}
                  <b>{meta.title}</b>
                </div>
                <button type="button" className="kbar" onClick={() => setPal(true)} aria-label="Search or jump to">
                  <IcSearch />
                  <span className="lbl2" style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Search buses, children, drivers…</span>
                  <span className="kbd">Ctrl K</span>
                </button>
                <a className="ibtn" aria-label={unread ? `Alerts, ${unread} new` : 'Alerts'} {...go('ConsoleAlerts')}>
                  <span><Svg size={20}><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></Svg></span>
                  {unread > 0 && <span className="badge">{unread}</span>}
                </a>
                <button type="button" className={'zbtn' + (zOpen ? ' on' : '')} onClick={() => setZOpen((o) => !o)} aria-label="Ask Zana" aria-expanded={zOpen}>
                  <ZanaMark size={20} />
                  <span className="lbl">Ask Zana</span>
                </button>
              </header>
              <main className="body" ref={bodyRef}>
                {!online && <div className="wrap" style={{ marginBottom: 16 }}><OfflineBanner /></div>}
                {children}
              </main>
              <ExplainLayer points={meta.points || []} ex={ex} set={setEx} />
              {(pickEl || (pointEl && !ex)) && (
                <PointRing el={(pickEl || pointEl)!.el} label={(pickEl || pointEl)!.label} />
              )}
              <TakeoverLayer />
            </div>
            <ZanaPanel open={zOpen} meta={meta} z={z} onClose={() => setZOpen(false)} onAdd={onAdd} onAsk={ask} ex={ex} setEx={setEx}
              chats={chats} activeId={activeId} onPick={onPick} onCloseChat={onCloseChat} chatLimit={chatLimit} onDraft={onDraft}
              wide={zWide} onWide={() => setZWide((w) => !w)} onRate={onRate} />
          </div>
          {pal && <Palette f={f} onClose={() => setPal(false)} onAsk={ask} />}
          {addBusOpen && (
            <AddBusModal f={f} onClose={() => setAddBusOpen(false)}
              onAdded={(plate) => navigate(`${PAGES.ConsoleBuses}?bus=${encodeURIComponent(plate)}`)} />
          )}
          <ToastRegion list={toasts.list} dismiss={toasts.api.dismiss} right={zOpen ? 404 : 24} />
        </div>
       </TakeoverProvider>
      </ToastProvider>
    </ShellCtx.Provider>
  );
};

export default Shell;
